import { filterClasses } from "@swivl-mono/helpers";
import { PolicyReviewGroup } from "@swivl-mono/types";
import { Button, ButtonColor, Modal, SelectItem } from "@swivl-mono/ui";
import { useMemo, useState } from "react";
import { FiArrowLeft, FiCheck } from "react-icons/fi";

import { AffectedLocationsDetail, AffectedLocationsField } from "./AffectedLocationsPanel";
import {
  DefaultOrCustomizedBadge,
  PolicyReviewHandlers,
  isCurrentValueDefault,
  nestGroups,
  proposedSummary,
  sectionizeGroups,
  todaySourceText,
  todaySummary
} from "./PolicyUpdatesList";
import { PolicyValueControl } from "./PolicyValueControl";

/** One walkthrough step: a top-level group plus any gated children folded under it (nestGroups —
 * the exact same fold "All updates" uses), so a child never gets its own separate, unapprovable step. */
interface WalkStep {
  group: PolicyReviewGroup;
  children: PolicyReviewGroup[];
}

interface PolicyWalkThroughProps {
  groups: PolicyReviewGroup[];
  /** Every org location, grouped by org group — for the "Affects N locations" multi-select. */
  allLocationOptions: SelectItem[];
  /** null = default to the first not-yet-approved update, or the first update if everything's approved. */
  startGroupKey: string | null;
  handlers: PolicyReviewHandlers;
  onClose: () => void;
}

function isGroupApplied(group: PolicyReviewGroup): boolean {
  return group.scope === "org" ? !!group.orgApplied : group.applied;
}

/**
 * The single cross-location review modal — one instance for every update, not one per location.
 * Left rail = table of contents + progress; right pane = one update at a time. Reads/writes the
 * exact same PolicyReviewHandlers as the "All updates" list, so a skip made in either view shows
 * immediately in the other once the shared `changes` state refreshes.
 */
export function PolicyWalkThrough({ groups, allLocationOptions, startGroupKey, handlers, onClose }: PolicyWalkThroughProps): React.ReactElement {
  // Sectioned the same way "All updates" is, then folded the same way — a gated child (e.g. grace
  // period's "how many days") is never its own step; it rides inside its parent's.
  const sections = sectionizeGroups(groups);
  const sectionSteps = sections.map((section) => ({ label: section.label, steps: nestGroups(section.groups) }));
  const steps: WalkStep[] = sectionSteps.flatMap((section) => section.steps);
  const indexByGroupKey = new Map(steps.map((step, i) => [step.group.groupKey, i]));

  const startIndex = useMemo(() => {
    if (startGroupKey) {
      // startGroupKey may name the parent OR a child (a child's own "All updates" row can open the
      // walkthrough too) — either way it should land on the step that now contains it.
      const found = steps.findIndex(
        (step) => step.group.groupKey === startGroupKey || step.children.some((child) => child.groupKey === startGroupKey)
      );
      if (found >= 0) return found;
    }
    const firstUnapplied = steps.findIndex((step) => !isGroupApplied(step.group));
    return firstUnapplied >= 0 ? firstUnapplied : 0;
    // Only the modal's initial open should pick a starting step — after that the operator drives navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [index, setIndex] = useState(startIndex);

  const appliedCount = steps.filter((step) => isGroupApplied(step.group)).length;
  const safeIndex = Math.min(index, steps.length - 1);
  const step = steps[safeIndex];
  const group = step?.group;
  const children = step?.children ?? [];
  const isLast = safeIndex === steps.length - 1;
  const isFirst = safeIndex === 0;
  const busy = group ? handlers.busyGroupKeys.has(group.groupKey) : false;

  const goTo = (next: number): void => {
    setIndex(Math.max(0, Math.min(next, steps.length - 1)));
  };

  const isApplied = group ? isGroupApplied(group) : false;
  // A gated child is never its own step (folded above), so this step can always approve — the
  // child's mapKey rides along in the same call, exactly like "All updates"' own folded rows.
  const canApproveHere = !!group && !isApplied;
  // Awaits the write before advancing/closing — closing on the last step used to fire the instant
  // the click happened, before the request even started, so a failure only ever surfaced as a toast
  // after the modal (which by then looked like it had finished) was already gone.
  const handleApproveAndNext = async (): Promise<void> => {
    if (group)
      await handlers.onApprove(
        group,
        children.map((child) => child.mapKey)
      );
    if (isLast) onClose();
    else goTo(safeIndex + 1);
  };

  if (!group) return <></>;

  return (
    <Modal
      isOpen
      onClose={onClose}
      hasTitleBorder
      title="Review policy fields"
      className="flex h-[min(86vh,760px)] w-[1040px] max-w-[calc(100vw-64px)] flex-col overflow-hidden"
    >
      <div className="flex min-h-0 flex-1">
        <div className="flex w-[300px] shrink-0 flex-col border-r border-grey-200 bg-grey-50">
          <div className="border-b border-grey-200 px-5 pt-5 pb-3.5">
            <div className="font-poppins text-[17px] font-semibold text-grey-900">Policy updates</div>
            <div className="mt-1 text-[12px] text-grey-500 tabular-nums">
              Step {safeIndex + 1} of {steps.length} · {appliedCount} approved
            </div>
            <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-grey-200">
              <div
                className="h-full rounded-full bg-orange-500 transition-all"
                style={{ width: `${steps.length === 0 ? 0 : (appliedCount / steps.length) * 100}%` }}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {sectionSteps.map((section) => {
              const doneInSection = section.steps.filter((s) => isGroupApplied(s.group)).length;
              return (
                <div key={section.label}>
                  <div className="flex items-baseline justify-between px-5 pt-3 pb-1.5">
                    <span className="text-[10px] font-bold tracking-[0.06em] text-grey-500 uppercase">{section.label}</span>
                    <span className="text-[11px] text-grey-500 tabular-nums">
                      {doneInSection}/{section.steps.length}
                    </span>
                  </div>
                  {section.steps.map((sectionStep) => {
                    const globalIndex = indexByGroupKey.get(sectionStep.group.groupKey) ?? 0;
                    const done = isGroupApplied(sectionStep.group);
                    const isActive = globalIndex === safeIndex;
                    const count = sectionStep.group.scope === "locations" ? sectionStep.group.locations.length : null;
                    return (
                      <button
                        key={sectionStep.group.groupKey}
                        type="button"
                        onClick={() => goTo(globalIndex)}
                        className={filterClasses(
                          "flex w-full items-center gap-2.5 px-5 py-2 text-left transition-colors hover:bg-grey-100",
                          isActive && "bg-white font-semibold text-grey-900 shadow-[inset_3px_0_0_var(--color-orange-500)]"
                        )}
                      >
                        <span
                          className={filterClasses(
                            "flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                            done ? "bg-teal-800 text-white" : isActive ? "bg-orange-500 text-white" : "bg-grey-200 text-grey-500"
                          )}
                        >
                          {done ? <FiCheck className="size-3" /> : globalIndex + 1}
                        </span>
                        <span title={sectionStep.group.label} className="min-w-0 flex-1 truncate text-[13px]">
                          {sectionStep.group.label}
                        </span>
                        {count !== null && <span className="shrink-0 text-[11px] text-grey-500 tabular-nums">{count} locs</span>}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="border-b border-grey-200 px-7 py-5">
            <div className="text-[11px] font-bold tracking-[0.06em] text-grey-500 uppercase">{group.policyGroup}</div>
            <div className="font-poppins text-[22px] font-semibold text-grey-900">{group.label}</div>
            {group.policyTitle && <div className="text-[13px] text-grey-500">{group.policyTitle}</div>}
          </div>

          <div className="flex-1 overflow-y-auto px-7 py-6">
            <div className="grid grid-cols-[1fr_24px_1fr] items-stretch gap-3">
              <div className="rounded-[5px] border border-grey-200 bg-white p-3.5">
                <div className="text-[10px] font-bold tracking-[0.06em] text-grey-500 uppercase">Today</div>
                {todaySourceText(group) ? (
                  <div
                    className="mt-1 text-[14px] leading-[19px] [overflow-wrap:anywhere] text-grey-500 italic"
                    title="The legacy text this proposed value was extracted from"
                  >
                    “{todaySourceText(group)}”
                  </div>
                ) : (
                  <div className="mt-1 text-[15px] leading-[21px] text-grey-600">{todaySummary(group)}</div>
                )}
              </div>
              <div className="flex items-center justify-center text-[16px] font-bold text-orange-500">→</div>
              <ValueCard group={group} childGroups={children} handlers={handlers} />
            </div>

            {/* Keyed off `locations.length`, not `scope` — see the matching PolicyUpdatesList note. */}
            {group.locations.length > 0 && (
              <div className="mt-6 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3 rounded-[5px] border border-grey-200 bg-grey-50 px-4 py-3">
                  <span className="text-[13px] font-semibold text-grey-900">Locations</span>
                  <AffectedLocationsField
                    group={group}
                    allLocationOptions={allLocationOptions}
                    busy={busy}
                    onToggleExcluded={(locationIds, excluded) => handlers.onToggleExcluded(group, locationIds, excluded)}
                    onAddLocations={(locationIds) => handlers.onAddLocations(group, locationIds)}
                  />
                </div>
                <AffectedLocationsDetail
                  group={group}
                  childGroups={children}
                  busy={busy}
                  onEditValue={(value, locationId, targetGroup) => handlers.onEditValue(targetGroup ?? group, value, locationId)}
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-grey-200 bg-grey-50 px-7 py-4">
            <button
              type="button"
              disabled={isFirst}
              onClick={() => goTo(safeIndex - 1)}
              className={filterClasses(
                "flex items-center gap-1.5 text-[13px] font-semibold",
                isFirst ? "text-grey-400" : "text-grey-600 hover:text-grey-900"
              )}
            >
              <FiArrowLeft className="size-3.5" /> Previous
            </button>
            <div className="flex items-center gap-2">
              <Button color={ButtonColor.WHITE} onClick={isLast ? onClose : () => goTo(safeIndex + 1)} disabled={busy}>
                {isLast ? "Close" : "Skip for now"}
              </Button>
              {canApproveHere && (
                <Button color={ButtonColor.ORANGE} onClickAsync={handleApproveAndNext} disabled={busy || handlers.approving}>
                  {isLast ? "Approve & finish" : "Approve & next"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

interface ValueCardProps {
  group: PolicyReviewGroup;
  /** Gated fields folded under `group` (nestGroups) — shown inline underneath, each with its own
   * real widget, so a resolved parent+child pair (e.g. "Set a specific number of days" + "5") reviews
   * as one unit instead of the child being an unreachable, unlinked second step. */
  childGroups: PolicyReviewGroup[];
  handlers: PolicyReviewHandlers;
}

/**
 * The "From document" box's proposed-value widget — the field's REAL Policy Builder control
 * (toggle/radio/checkboxes), so review here feels like using the Policy page itself rather than a
 * separate text-editing UI. Unlike "All updates"' dense row, this card has room for the full-size
 * PolicyRadio/checkbox-list widgets — no compact variant needed here. When the group's locations
 * disagree (`proposedValue === null`), shows "Varies by location" — matching how "Today" already
 * reads when THEY disagree — and isn't editable here; correcting an individual location happens in
 * the per-location breakdown below, which is always live regardless of scope.
 */
function ValueCard({ group, childGroups, handlers }: ValueCardProps): React.ReactElement {
  const busy = handlers.busyGroupKeys.has(group.groupKey);
  return (
    <div className="rounded-[5px] border border-orange-200 bg-orange-50 p-3.5">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold tracking-[0.06em] text-orange-700 uppercase">From document</div>
        <DefaultOrCustomizedBadge isDefault={isCurrentValueDefault(group)} />
      </div>
      <div className="mt-2">
        {group.proposedValue === null ? (
          <span className="text-[15px] text-grey-500 italic">{proposedSummary(group)}</span>
        ) : (
          <PolicyValueControl
            fieldType={group.fieldType}
            options={group.options}
            value={group.proposedValue}
            busy={busy}
            onChange={(value) => handlers.onEditValue(group, value)}
          />
        )}
      </div>
      {group.citation && <div className="mt-2 text-[12px] text-grey-500 italic">“{group.citation}”</div>}

      {childGroups.length > 0 && (
        <div className="mt-3 flex flex-col gap-3 border-t border-orange-200 pt-3">
          {childGroups.map((child) => {
            const childBusy = handlers.busyGroupKeys.has(child.groupKey);
            return (
              <div key={child.groupKey} className="flex flex-col gap-1.5">
                <div className="text-[11px] font-semibold text-grey-700">{child.label}</div>
                {child.proposedValue === null ? (
                  <span className="text-[13px] text-grey-500 italic">{proposedSummary(child)}</span>
                ) : (
                  <PolicyValueControl
                    fieldType={child.fieldType}
                    options={child.options}
                    value={child.proposedValue}
                    busy={childBusy}
                    onChange={(value) => handlers.onEditValue(child, value)}
                  />
                )}
                {child.citation && child.citation !== group.citation && <div className="text-[12px] text-grey-500 italic">“{child.citation}”</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
